import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import BetBonusPromotions from 'modules/bet-bonus-promotions/index.js';
import BetViewUtils from 'modules/your-bets/bet-view/utils.js';
import OddsBoostIndicator from 'modules/odds-boost-indicator/index.js';
import ShareBet from 'modules/share-bet/index.js';
import BetLegsDetails from 'modules/bet-legs-details/index.js';
import DownloadBetPdfButton from 'modules/download-bet-pdf-button/index.js';

const { FormattedLabelWithTotalOdds } = MojitoPresentation.Components.FormattedText;
const { UIViewImplementation } = MojitoCore.Presentation;
const { BettingHelper } = MojitoPresentation.Utils;
const { utils: BetsUtils, types: BetsTypes } = MojitoServices.Bets;
const EventUtils = MojitoServices.SportsContent.Events.utils;
const BetslipUtils = MojitoServices.Betslip.utils;

const {
    Text,
    FlexPane,
    Image,
    Button,
    ScrollPane,
    DateTime,
    BetFunds,
    AbsolutePane,
    VoidReasonMessage,
    BetStatusIcon,
} = MojitoPresentation.Components;
const {
    PayableInfo,
    FreebetStakeInfo,
    CashStakeInfo,
    StakeTaxInfo,
    StakeInfo,
    PrewagerStakeInfo,
    TotalPayoutInfo,
    PayoutInfo,
} = BetFunds;

export default class BetHistoryDetails extends UIViewImplementation {
    constructor(props) {
        super(props);
        this.state = {
            isScrolled: false,
        };
        this.handleScroll = this.handleScroll.bind(this);
        this.onDownloadBetPdfClicked = () => {
            this.emitAnalytics('betHistoryPrint');
        };
    }

    handleScroll(scrollInfo) {
        if (!this.config.showCompactLayout) {
            const { scrollPosition } = scrollInfo;
            if (scrollPosition > 0 && !this.state.isScrolled) {
                this.setState({ isScrolled: true });
            } else if (scrollPosition <= 0) {
                this.setState({ isScrolled: false });
            }
        }
    }

    getHeaderStyle() {
        if (this.state.isScrolled) {
            return {
                style: {
                    ...this.config.header.style,
                    ...this.config.style.headerScrolled,
                },
            };
        }
        return this.config.header;
    }

    getBetTypeName(bet) {
        const { betType, isTeaser, isBBAcca } = bet;
        const betFold = BetsTypes.BET_TYPE_TO_INDEX[betType];
        const isPlural = BettingHelper.isPlural(bet);
        const numberOfLines = bet.numberOfLines;
        const numberOfEWLines = bet.numberOfEWLines;
        return BettingHelper.getTypeName(
            betType,
            this.l10n,
            isTeaser,
            isBBAcca,
            isPlural,
            betFold,
            numberOfLines,
            numberOfEWLines
        );
    }

    resolveBetModifiers(bet) {
        return BettingHelper.getBetModifiers(bet, this.l10n);
    }

    renderTotalOdds(bet) {
        const { odds, baseOdds, priceType } = bet;
        return (
            <FormattedLabelWithTotalOdds
                odds={EventUtils.getDisplayedOdds(odds, priceType, this.l10n)}
                baseOdds={priceType !== BetsTypes.PRICE_TYPE.SP ? baseOdds : undefined}
                isTotalOddsApplicable={BetsUtils.isTotalOddsApplicable(bet)}
                config={this.config.totalOdds}
            />
        );
    }

    renderHeader(bet) {
        const { status, betType, placementTime } = bet;
        const betTypeName = this.getBetTypeName(bet);
        const iconStatus = BetViewUtils.getStatusIcon(status);
        const isMatchAcca = BetsUtils.hasMatchAccaLeg(bet);
        const isOddsBoostBet =
            BetsUtils.hasOddsBoost(bet) && bet.betType !== BetsTypes.BET_TYPE.SINGLE;

        return (
            <FlexPane config={this.getHeaderStyle()}>
                <FlexPane config={this.config.betTypeContainer} class="ta-betHeader">
                    <Text
                        config={this.config.betTypeText}
                        class={`ta-${betType}${isMatchAcca ? ' ta-matchAcca' : ''}`}
                    >{`${betTypeName} ${this.resolveBetModifiers(bet)}`}</Text>
                    {this.renderTotalOdds(bet)}
                    {isOddsBoostBet && (
                        <OddsBoostIndicator config={this.config.oddsBoostIndicator} bet={bet} />
                    )}
                    {this.renderBonuses(bet)}
                    <Text config={this.config.dateText} class="ta-dateText">
                        <DateTime
                            config={this.config.betPlacementDateTimeFormat}
                            dateTime={placementTime}
                        />
                    </Text>
                </FlexPane>
                <FlexPane config={this.config.betStatusContainer} class="ta-betStatusContainer">
                    <BetStatusIcon config={this.config.betStatusIcon} status={iconStatus} />
                    <Text config={this.config.betStatusText} class="ta-betStatus">
                        {BettingHelper.getBetStatusString(bet, this.l10n)}
                    </Text>
                    {!this.config.showCompactLayout ? (
                        <Button
                            onClick={this.props.onCloseClick}
                            config={this.config.closeButton}
                            class="ta-closeBtn"
                        >
                            <Image config={this.config.closeIcon} />
                        </Button>
                    ) : null}
                </FlexPane>
            </FlexPane>
        );
    }

    renderBonuses(bet) {
        const { bonuses = [] } = bet;
        const { currency } = bet.funds ?? {};

        if (!bonuses.length) {
            return null;
        }

        return (
            <BetBonusPromotions
                config={this.config.betBonusPromotions}
                bonuses={bet.bonuses}
                currencyCode={currency}
            />
        );
    }

    renderBetHeaderInfo(bet) {
        const { funds, numberOfLines } = bet;
        const { stakePerLine, currency } = funds;
        const valueNotAvailable = this.resolveString('$BET_HISTORY_DETAILS.VALUE_NOT_AVAILABLE');
        const stakeLabel = BettingHelper.formatPayout(stakePerLine, valueNotAvailable, currency);
        const separator = this.resolveString('$BET_HISTORY_DETAILS.BET_SEPARATOR_SYMBOL');
        const valueSeparator = this.resolveString('$BET_HISTORY_DETAILS.VALUE_SEPARATOR');
        return (
            <FlexPane config={this.config.betHeaderInfoContainer} class="ta-betHeaderInfoContainer">
                <Text config={this.config.betLabel} class="ta-betLabel">
                    {this.resolveAndFormatString(
                        '$BET_HISTORY_DETAILS.BET_HEADER',
                        valueSeparator,
                        stakeLabel,
                        separator,
                        numberOfLines
                    )}
                </Text>
            </FlexPane>
        );
    }

    renderBetFunds(bet) {
        const { funds } = bet;
        const { currency, prewager } = funds;
        const {
            stake,
            stakeAfterTaxes,
            stakeTax,
            freebetStake,
            cashStake = 0,
            boostReturns,
        } = funds;
        const boost = BetsUtils.getOddsBoost(bet);
        const { contributionSource, oddsBoostSubtype } = boost || {};
        const payoutInfoLabel = BetslipUtils.resolveBoostLabel(
            contributionSource,
            oddsBoostSubtype,
            {
                PROFIT: '$BET_FUNDS.BOOST_PROFIT',
                RETURN: '$BET_FUNDS.BOOST_RETURN',
                FIXED: '$BET_FUNDS.BOOST_FIXED',
            }
        );

        return (
            <FlexPane config={this.config.betFundsContainer}>
                {stakeTax > 0 ? (
                    <PayableInfo
                        config={this.config.totalPayable}
                        value={stake}
                        currencyCode={currency}
                    />
                ) : (
                    <StakeInfo
                        config={this.config.primaryTotalStake}
                        currencyCode={currency}
                        value={stake}
                    />
                )}
                {freebetStake > 0 && (
                    <>
                        <CashStakeInfo
                            config={this.config.cashStake}
                            value={cashStake}
                            currencyCode={currency}
                        />
                        <FreebetStakeInfo
                            config={this.config.freebetStake}
                            value={freebetStake}
                            currencyCode={currency}
                        />
                    </>
                )}
                {prewager && (
                    <PrewagerStakeInfo
                        config={this.config.prewager}
                        prewager={prewager}
                        currencyCode={currency}
                    />
                )}
                {stakeTax > 0 && (
                    <>
                        <StakeTaxInfo
                            config={this.config.stakeTax}
                            value={stakeTax}
                            currencyCode={currency}
                        />
                        <StakeInfo
                            config={this.config.secondaryTotalStake}
                            currencyCode={currency}
                            value={stakeAfterTaxes}
                        />
                    </>
                )}
                <TotalPayoutInfo
                    config={this.config.totalPayoutInfo}
                    funds={{ ...funds, currency, prewager }}
                    isPotential={BetsUtils.isOpen(bet)}
                    isBoosted={BetsUtils.hasAccaBoost(bet)}
                />
                {boostReturns > 0 && (
                    <PayoutInfo
                        config={this.config.oddsBoostInfo}
                        payout={boostReturns}
                        labelKey={payoutInfoLabel}
                        currencyCode={currency}
                    />
                )}
            </FlexPane>
        );
    }

    renderSettledBetInfo(bet) {
        if (!this.props.bet.hasOwnProperty('settled')) {
            return null;
        }
        const settled = bet.settled;

        return (
            <FlexPane config={this.config.settleBetInfoContainer}>
                <Text class="ta-settledBetLabel" config={this.config.settleBetLabel}>
                    {this.resolveString('$BET_HISTORY_DETAILS.SETTLED')}
                </Text>
                {!!settled && (
                    <Text class="ta-settledBetValue" config={this.config.settleBetValue}>
                        <DateTime config={this.config.settledDateTimeFormat} dateTime={settled} />
                    </Text>
                )}
            </FlexPane>
        );
    }

    renderBetInfo(bet) {
        return (
            <FlexPane config={this.config.betInfo} class="ta-betInfo">
                {this.renderReceiptInfo(bet)}
                {this.renderBetHeaderInfo(bet)}
                <FlexPane config={this.config.betFundsAndBonusesContainer}>
                    {this.renderBetFunds(bet)}
                    <VoidReasonMessage
                        config={this.config.voidReasonMessage}
                        reason={bet.voidReason}
                    />
                </FlexPane>
            </FlexPane>
        );
    }

    renderDetails(bet) {
        return (
            <>
                <Text config={this.config.detailsLabel} class="ta-detailsLabel">
                    {this.resolveString('$BET_HISTORY_DETAILS.DETAILS')}
                </Text>
                <BetLegsDetails config={this.config.betLegsDetails} bet={bet} />
            </>
        );
    }

    renderReceiptInfo(bet) {
        return (
            <FlexPane config={this.config.receiptInfo} class="ta-receiptInfo">
                <FlexPane config={this.config.receiptTextInfo}>
                    <Text config={this.config.receiptLabel} class="ta-receiptLabel">
                        {this.resolveString('$BET_HISTORY_DETAILS.RECEIPT')}
                        {this.resolveString('$BET_HISTORY_DETAILS.VALUE_SEPARATOR')}
                    </Text>
                    <Text config={this.config.receiptNumber} class="ta-receiptNumber">
                        {bet.id}
                    </Text>
                </FlexPane>
                {bet.deviceId && (
                    <FlexPane config={this.config.receiptTextInfo}>
                        <Text config={this.config.deviceIdLabel} class="ta-deviceIdLabel">
                            {this.resolveString('$BET_HISTORY_DETAILS.DEVICE_ID')}
                            {this.resolveString('$BET_HISTORY_DETAILS.VALUE_SEPARATOR')}
                        </Text>
                        <Text config={this.config.deviceIdNumber} class="ta-deviceIdNumber">
                            {bet.deviceId}
                        </Text>
                    </FlexPane>
                )}
                <FlexPane config={this.config.receiptTextInfo}>
                    <Text config={this.config.channelLabel} class="ta-channelLabel">
                        {this.resolveString('$BET_HISTORY_DETAILS.CHANNEL')}
                        {this.resolveString('$BET_HISTORY_DETAILS.VALUE_SEPARATOR')}
                    </Text>
                    <Text config={this.config.channelValue} class="ta-channelValue">
                        {bet.channel?.name ||
                            this.resolveString(
                                `$BET_HISTORY_DETAILS.CHANNEL_TYPE.${bet.channel?.code}`
                            )}
                    </Text>
                </FlexPane>
                {this.renderSettledBetInfo(bet)}
            </FlexPane>
        );
    }

    renderPdfError() {
        if (this.props.pdfData.hasError) {
            return (
                <FlexPane config={this.config.errorContainer}>
                    <Text config={this.config.errorLabel}>
                        {this.resolveString('$BET_VIEW.PDF_ERROR')}
                    </Text>
                </FlexPane>
            );
        }
    }

    renderShareContainer(bet) {
        const { isFetching: isPdfFetching } = this.props.pdfData;

        return (
            <AbsolutePane config={this.config.shareContainer} class="ta-BetHistoryDetailsShareBet">
                <ShareBet config={this.config.shareBet} bet={bet} />

                {!this.config.hidePdfButton && (
                    <DownloadBetPdfButton
                        isFetching={isPdfFetching}
                        bets={[bet]}
                        onClick={this.onDownloadBetPdfClicked}
                        config={this.config.downloadBetPdfButton}
                    />
                )}
            </AbsolutePane>
        );
    }

    render() {
        const { bet } = this.props;
        const betContent = (
            <>
                {this.renderBetInfo(bet)}
                {this.renderDetails(bet)}
                {this.renderPdfError()}
            </>
        );
        return (
            <FlexPane config={this.config.content} class="ta-detailsPage">
                {this.renderShareContainer(bet)}
                {this.renderHeader(bet)}
                {!this.config.showCompactLayout ? (
                    <ScrollPane config={this.config.betInfoContent} onScroll={this.handleScroll}>
                        {betContent}
                    </ScrollPane>
                ) : (
                    betContent
                )}
            </FlexPane>
        );
    }
}
